// Shrine's slider code also expects a global debounce helper. The theme's
// placeholder asset is empty, so provide the missing dependency once without
// coupling thumbnail initialization to it.
if (typeof window.debounce !== 'function') {
  window.debounce = (callback, wait = 0) => {
    let timeoutId;
    return function debounced(...args) {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => callback.apply(this, args), wait);
    };
  };
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      if (this.productInfo && Shopify.postLinksRetry && typeof this.productInfo.initShareLinks === 'function') {
        this.productInfo.initShareLinks();
      }

      // Thumbnail selection is initialized before optional gallery features so
      // a missing secondary dependency can never disable tap/click selection.
      if (this.elements.thumbnails) this.initThumbnailSelection();

      if (this.elements.viewer) {
        this.elements.viewer.addEventListener('slideChanged', this.onSlideChanged.bind(this));
      }

      if (
        this.elements.thumbnails &&
        (this.dataset.desktopLayout || '').includes('thumbnail') &&
        this.mql.matches
      ) {
        this.removeListSemantic();
      }
    }

    initThumbnailSelection() {
      const thumbnails = this.elements.thumbnails;
      this.thumbnailPointer = null;
      this.ignoreSyntheticClickUntil = 0;

      thumbnails.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
        const thumbnail = event.target.closest('[data-target]');
        if (!thumbnail || !thumbnails.contains(thumbnail)) return;

        this.thumbnailPointer = {
          pointerId: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          startedAt: Date.now(),
          thumbnail
        };
      });

      thumbnails.addEventListener('pointercancel', (event) => {
        if (this.thumbnailPointer && this.thumbnailPointer.pointerId === event.pointerId) {
          this.thumbnailPointer = null;
        }
      });

      thumbnails.addEventListener('pointerup', (event) => {
        const start = this.thumbnailPointer;
        this.thumbnailPointer = null;
        if (!start || start.pointerId !== event.pointerId) return;

        const thumbnail = event.target.closest('[data-target]');
        if (!thumbnail || thumbnail !== start.thumbnail || !thumbnails.contains(thumbnail)) return;

        const dx = Math.abs(event.clientX - start.x);
        const dy = Math.abs(event.clientY - start.y);
        const duration = Date.now() - start.startedAt;
        if (dx > 12 || dy > 12 || duration > 600) return;

        this.ignoreSyntheticClickUntil = Date.now() + 700;
        this.setActiveMedia(thumbnail.dataset.target, false);
      });

      thumbnails.addEventListener('click', (event) => {
        const thumbnail = event.target.closest('[data-target]');
        if (!thumbnail || !thumbnails.contains(thumbnail)) return;

        // Keyboard-generated clicks have detail === 0 and must remain active.
        if (event.detail !== 0 && Date.now() < this.ignoreSyntheticClickUntil) return;
        this.setActiveMedia(thumbnail.dataset.target, false);
      });
    }

    onSlideChanged(event) {
      if (!this.elements.thumbnails || !event.detail || !event.detail.currentElement) return;
      const mediaId = event.detail.currentElement.dataset.mediaId;
      if (!mediaId) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      if (!mediaId || !this.elements.viewer) return;
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail && activeThumbnail.parentElement) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }
        if (this.elements.viewer.slider && typeof this.elements.viewer.resetPages === 'function') {
          this.elements.viewer.resetPages();
        }
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails && activeMedia.parentElement) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({ behavior: 'smooth' });
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      if (!activeThumbnail) return;
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => {
        element.removeAttribute('aria-current');
      });
      const button = thumbnail.querySelector('button');
      if (button) button.setAttribute('aria-current', 'true');

      if (
        typeof this.elements.thumbnails.isSlideVisible === 'function' &&
        this.elements.thumbnails.isSlideVisible(thumbnail, 10)
      ) return;

      if (
        this.elements.thumbnails.slider &&
        typeof this.elements.thumbnails.slider.scrollTo === 'function'
      ) {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
    }

    announceLiveRegion(activeItem, position) {
      if (!this.elements.liveRegion || !window.accessibilityStrings) return;
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
        deferredMedia.loadContent(false);
      }
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer || !this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      if (this.elements.viewer.sliderItems) {
        this.elements.viewer.sliderItems.forEach((slide) => {
          slide.setAttribute('role', 'presentation');
        });
      }
    }
  });
}
